import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Button, Badge, Spinner, Alert,
    Form, InputGroup, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";


export default function Echeancier() {
    const navigate = useNavigate();
    const [echeances, setEcheances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    
    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchEcheances();
        fetchStats();
    }, []);

    const fetchEcheances = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/reglements.php?action=echeancier`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setEcheances(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/reglements.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const getDelaiBadge = (jours) => {
        if (jours < 0) {
            return (
                <Badge className="px-3 py-2 rounded-pill" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: 'white' }}>
                    <i className="ri-alarm-warning-line me-1"></i>
                    En retard ({Math.abs(jours)}j)
                </Badge>
            );
        } else if (jours <= 7) {
            return (
                <Badge className="px-3 py-2 rounded-pill" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white' }}>
                    <i className="ri-time-line me-1"></i>
                    Proche ({jours}j)
                </Badge>
            );
        } else {
            return (
                <Badge className="px-3 py-2 rounded-pill" style={{ background: 'linear-gradient(135deg, #0ea5e9, #0284c7)', color: 'white' }}>
                    <i className="ri-calendar-line me-1"></i>
                    Dans {jours} jours
                </Badge>
            );
        }
    };

    const getStatutFactureBadge = (statut) => {
        const configs = {
            emise: { bg: '#3b82f6', icon: 'ri-send-plane-line', label: 'Émise' },
            payee_partielle: { bg: '#f59e0b', icon: 'ri-time-line', label: 'Paiement partiel' },
            payee: { bg: '#10b981', icon: 'ri-check-double-line', label: 'Payée' },
            impayee: { bg: '#ef4444', icon: 'ri-error-warning-line', label: 'Impayée' }
        };
        const config = configs[statut] || { bg: '#64748b', icon: 'ri-question-line', label: statut };
        return (
            <span className="status-badge" style={{ background: `${config.bg}15`, color: config.bg }}>
                <i className={`${config.icon} me-1 fs-10`}></i>
                {config.label}
            </span>
        );
    };

    const filteredEcheances = echeances.filter(e => 
        e.numero_facture?.toLowerCase().includes(filterText.toLowerCase()) ||
        e.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
        e.code_client?.toLowerCase().includes(filterText.toLowerCase())
    );

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredEcheances.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredEcheances.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const totalImpayes = filteredEcheances.reduce((sum, e) => sum + parseFloat(e.solde || 0), 0);
    const totalRetards = filteredEcheances.filter(e => e.jours_restant < 0).length;
    const totalEcheancesProches = filteredEcheances.filter(e => e.jours_restant <= 7 && e.jours_restant >= 0).length;

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement de l'échéancier...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchEcheances}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-calendar-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Échéancier</h4>
                                    <p className="text-muted small mb-0">Suivi des factures impayées et échéances</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/ventes/reglements')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour aux règlements
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards */}
                    <Row className="g-3 mb-4">
                        {/* Carte Total impayés */}
                        <Col sm={6} xl={4}>
                            <div className="kpi-card kpi-danger">
                                <div className="kpi-icon">
                                    <i className="ri-error-warning-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Total impayés</span>
                                    <h3 className="kpi-value">{formatCurrency(totalImpayes)}</h3>
                                    <span className="kpi-unit">{filteredEcheances.length} facture(s)</span>
                                </div>
                            </div>
                        </Col>

                        {/* Carte En retard */}
                        <Col sm={6} xl={4}>
                            <div className="kpi-card kpi-warning">
                                <div className="kpi-icon">
                                    <i className="ri-alarm-warning-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">En retard</span>
                                    <h3 className="kpi-value">{totalRetards}</h3>
                                    <span className="kpi-unit">Action immédiate</span>
                                </div>
                            </div>
                        </Col>

                        {/* Carte Échéances critiques */}
                        <Col sm={6} xl={4}>
                            <div className="kpi-card kpi-info">
                                <div className="kpi-icon">
                                    <i className="ri-time-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Échéances critiques</span>
                                    <h3 className="kpi-value">{totalEcheancesProches}</h3>
                                    <span className="kpi-unit">≤ 7 jours</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Filtre recherche */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md={6}>
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher par facture, client..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="text-end">
                                        <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                            <i className="ri-stack-line me-1"></i>
                                            {filteredEcheances.length} échéance(s)
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Tableau des échéances */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">N° Facture</th>
                                            {/* <th>Date facture</th> */}
                                            <th>Client</th>
                                            <th>Échéance</th>
                                            <th className="text-end">Montant TTC</th>
                                            <th className="text-end">Réglé</th>
                                            <th>Délai</th>
                                            <th>Statut</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((facture) => (
                                                <tr key={facture.id_facture} className={facture.jours_restant < 0 ? 'table-danger' : ''}>
                                                    <td className="ps-4">
                                                        <Link 
                                                            to={`/ventes/facturesDetails/${facture.id_facture}`} 
                                                            className="table-link"
                                                            title={facture.numero_facture}
                                                        >
                                                            {facture.numero_facture?.length > 12 
                                                                ? facture.numero_facture.substring(0, 12) + '...' 
                                                                : facture.numero_facture}
                                                        </Link>
                                                    </td>
                                                    {/* <td>{formatDate(facture.date_facture)}</td> */}
                                                    <td>
                                                        <div className="fw-semibold">{facture.client_nom}</div>
                                                        <small className="text-muted">{facture.code_client}</small>
                                                    </td>
                                                    <td>
                                                        <span className={facture.jours_restant < 0 ? 'text-danger fw-bold' : ''}>
                                                            {formatDate(facture.date_echeance)}
                                                        </span>
                                                    </td>
                                                    <td className="text-end fw-bold">{formatCurrency(facture.montant_ttc)}</td>
                                                    <td className="text-end text-success fw-semibold">{formatCurrency(facture.montant_regle)}</td>
                                                    <td>{getDelaiBadge(facture.jours_restant)}</td>
                                                    <td>{getStatutFactureBadge(facture.statut)}</td>
                                                    <td className="">
                                                        <Button
                                                            variant="success"
                                                            
                                                            size="sm"
                                                            className="rounded-pill"
                                                            onClick={() => navigate(`/ventes/NouveauReglement/?facture=${facture.id_facture}&client=${facture.id_client}`)}
                                                            style={{ background: '#10b981', border: 'none',minWidth:'150px' }}
                                                        >
                                                            <i className="ri-bank-card-line me-1"></i>
                                                            Saisir règlement
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="9" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-calendar-check-line empty-icon text-success" style={{ fontSize: '4rem' }}></i>
                                                        <h5 className="mt-3">Aucune échéance en cours</h5>
                                                        <p className="text-muted mb-3">Toutes les factures sont soldées</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredEcheances.length)} sur {filteredEcheances.length} échéances
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-danger { border-left-color: #ef4444; }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-info { border-left-color: #0ea5e9; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #0ea5e9; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Pagination */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                    box-shadow: 0 4px 10px rgba(33, 172, 205, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                .table-danger {
                    background-color: #fff5f5;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-14 {
                    font-size: 0.875rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
            `}</style>
        </>
    );
}